import type { ChecklistPointDto, ChecklistTemplateDto, IntakeContextDto } from '@reos/field-intake';
import type { ChecklistState } from './field-intake-checklist.js';

/**
 * Krok NAB2 — POCITANI NAD SABLONOU CHECKLISTU v prohlizeci.
 *
 * Sablona je data ze serveru (`GET /v1/field-intake/checklist-template`),
 * ale rozhodnuti „co se ted ukaze" a „Sreality X/Y · Smlouva X/Y" musi umet
 * i prohlizec: makler nabira BEZ SIGNALU a odpovedi lezi v offline fronte,
 * takze je server pri pocitani nevidi.
 *
 * Kontext `@reos/field-intake` smi web-admin importovat JEN jako typ
 * (`test/browser-safe-imports.spec.ts`), proto je to lokalni kopie pravidel
 * — stejny vzor jako `gate-catalog.ts` nebo `components/field-intake/fields.ts`.
 * Ze se s kontextem nerozesla, hlida test parity `intake-checklist.spec.ts`
 * (vola OBE implementace nad skutecnou sablonou).
 */

export type ChecklistMode = 'quick' | 'full';

/** Odpovedi checklistu: `id` bodu -> hodnota faktu. */
export type PointAnswers = ReadonlyMap<string, unknown>;

function sameValue(answer: unknown, expected: unknown): boolean {
  return Array.isArray(answer) ? answer.some((item) => item === expected) : answer === expected;
}

export function isPointVisible(point: ChecklistPointDto, answers: PointAnswers): boolean {
  const rule = point.visibleWhen;
  if (rule === null) return true;
  const value = answers.get(rule.pointId);
  if (value === undefined || value === null) return false;
  const hit = rule.values.some((expected) => sameValue(value, expected));
  return rule.op === 'eq' ? hit : !hit;
}

export function visiblePoints(
  template: ChecklistTemplateDto,
  answers: PointAnswers,
  mode: ChecklistMode,
): readonly ChecklistPointDto[] {
  return template.points.filter((point) => (mode === 'full' || point.quick) && isPointVisible(point, answers));
}

/** Bod, na ktery se v checklistu odpovida (ostatni udaje drzi jina cast REOS). */
export function isAnswerable(point: ChecklistPointDto): boolean {
  return point.factKey !== null;
}

export interface ChecklistCount {
  readonly done: number;
  readonly total: number;
  readonly missing: readonly ChecklistPointDto[];
}

export interface ChecklistProgress {
  readonly sreality: ChecklistCount;
  readonly contract: ChecklistCount;
}

function count(
  points: readonly ChecklistPointDto[],
  answered: (point: ChecklistPointDto) => boolean,
): ChecklistCount {
  const missing = points.filter((point) => !answered(point));
  return { done: points.length - missing.length, total: points.length, missing };
}

export function checklistProgress(
  template: ChecklistTemplateDto,
  answers: PointAnswers,
  covered: ReadonlySet<string> = new Set(),
): ChecklistProgress {
  const answered = (point: ChecklistPointDto): boolean =>
    covered.has(point.id) || (answers.get(point.id) !== undefined && answers.get(point.id) !== null);
  const inPlay = template.points.filter(
    (point) => isPointVisible(point, answers) && (isAnswerable(point) || covered.has(point.id)),
  );
  return {
    sreality: count(inPlay.filter((point) => point.sreality?.required === true), answered),
    contract: count(inPlay.filter((point) => point.contractKey !== null), answered),
  };
}

/**
 * Znacky naboru (klicovane faktem) -> odpovedi (klicovane bodem). Jeden bod =
 * jeden fakt (hlida test sablony), takze prevod je jednoznacny a stav
 * obrazovky, offline fronta ani zapis do zakazky se nemeni.
 */
export function pointAnswers(template: ChecklistTemplateDto, marks: ChecklistState): PointAnswers {
  const out = new Map<string, unknown>();
  for (const point of template.points) {
    if (point.factKey === null) continue;
    const mark = marks[point.factKey];
    if (mark === undefined || mark.value === undefined) continue;
    out.set(point.id, mark.value);
  }
  return out;
}

/**
 * Body, ktere REOS uz zna odjinud: adresu drzi zakazka (bod `providedBy:
 * property_address`) a fakta z provereni prichazi v `known`.
 */
export function coveredPoints(template: ChecklistTemplateDto, context: IntakeContextDto | null): ReadonlySet<string> {
  const out = new Set<string>();
  if (context === null) return out;
  const hasAddress = (context.case?.address ?? null) !== null;
  const known = new Set(context.known.map((fact) => fact.factKey));
  for (const point of template.points) {
    if (point.providedBy === 'property_address' && hasAddress) out.add(point.id);
    if (point.factKey !== null && known.has(point.factKey)) out.add(point.id);
  }
  return out;
}

/**
 * Hodnota faktu z provereni/zakazky pro predvyplneni bodu (se znackou puvodu).
 *
 * Krok P8F6 bod 1: vraci I `provenanceStatus`, protoze z obou dohromady se
 * teprve sklada LIDSKY puvod („katastr / tvrdí majitel / makléř",
 * `provenanceOrigin` v `lib/case-origin.ts`). Do P8F6 sel na obrazovku
 * surovy kod zdroje (`user`, `owner`) — a dispozicí z pruvodce by makler
 * cetl „Už víme: 3+1 (user)".
 */
export function prefillOf(
  point: ChecklistPointDto,
  context: IntakeContextDto | null,
): { readonly value: unknown; readonly sourceType: string; readonly provenanceStatus: string } | null {
  if (context === null || point.factKey === null) return null;
  const fact = context.known.find((candidate) => candidate.factKey === point.factKey);
  return fact === undefined
    ? null
    : { value: fact.value, sourceType: fact.sourceType, provenanceStatus: fact.provenanceStatus };
}

export interface GroupProgress {
  readonly id: string;
  readonly title: string;
  readonly points: readonly ChecklistPointDto[];
  readonly done: number;
}

/** Skupiny v poradi prohlidky, s pocitadlem „vyplneno/celkem". */
export function groupedPoints(
  template: ChecklistTemplateDto,
  answers: PointAnswers,
  mode: ChecklistMode,
  covered: ReadonlySet<string> = new Set(),
): readonly GroupProgress[] {
  const visible = visiblePoints(template, answers, mode);
  return template.groups
    .map((group) => {
      const points = visible.filter((point) => point.group === group.id);
      const done = points.filter(
        (point) => covered.has(point.id) || (answers.get(point.id) !== undefined && answers.get(point.id) !== null),
      ).length;
      return { id: group.id, title: group.title, points, done };
    })
    .filter((group) => group.points.length > 0);
}

/**
 * Krok P8F2 — HODNOTA BODU PRO ZAPIS DO RELACE NABORU (kopie pravidla
 * `checklistWriteValue` z kontextu, duvod kopie viz hlavicka souboru; parita
 * se meri v `intake-checklist-parita-sablon.spec.ts`).
 *
 * Multiselect drzi POLE hodnot, ale cilovy fakt muze byt `text`
 * (`heating_type`, `parking_type`) — server pole odmitne (422) a odpoved by
 * uvizla v cele fronty. Rozhoduje TYP FAKTU (`valueType` ze sablony, tedy
 * registr `FACT_KEYS`), ne seznam poli.
 */
export function checklistWriteValue(point: ChecklistPointDto, value: unknown): unknown {
  if (!Array.isArray(value) || point.valueType !== 'text') return value;
  return value.map((item) => String(item)).join(', ').slice(0, 500);
}
